import { ColumnSort, PaginationState } from "@tanstack/react-table";
import { get } from "lodash";

import {
  GROUPING_GRID_NO_GROUPS,
  TABLE_PAGE_SIZE_GROUPING,
  TABLE_PAGE_SIZE_UNGROUPED,
} from "../constants";
import type { IExtendableDto } from "../dto";
import { BulkGridSelectionModeEnum } from "../enums/bulk-grid-selection-mode.enum";
import type { ISearchCriteria } from "../models";
import { setsEqual } from "./array.helpers";
import { compareBasic, compareText } from "./sorting.helpers";
import {
  getTableFilteredGroupedRowsFromMap,
  TTableFilteredGroupedRowsMap,
} from "./table-filtered-rows.helpers";
import { tableHelpers } from "./table.helpers";

const toIdSet = (ids: readonly number[]): Set<number> => new Set(ids);

export const parseBulkGridSelectionRowCountDraft = ({
  raw,
  fallback,
}: {
  raw: string;
  fallback: number;
}): number => {
  const trimmed = raw.trim();

  if (!trimmed) {
    return fallback;
  }

  const parsed = Number.parseInt(trimmed, 10);

  if (Number.isNaN(parsed)) {
    return fallback;
  }

  return Math.max(0, Math.floor(parsed));
};

export const inferBulkGridSelectionMode = ({
  selectedIds,
  allIds,
  pageIds,
  rowCountIds,
  activeMode,
}: {
  selectedIds: readonly number[];
  allIds: readonly number[];
  pageIds: readonly number[];
  rowCountIds: readonly number[];
  activeMode?: BulkGridSelectionModeEnum;
}): BulkGridSelectionModeEnum | undefined => {
  const selectedSet = toIdSet(selectedIds);

  if (!selectedSet.size) {
    return undefined;
  }

  const pageSet = pageIds.length ? toIdSet(pageIds) : undefined;
  const matchesPage = pageSet !== undefined && setsEqual(selectedSet, pageSet);
  const matchesAll =
    allIds.length > 0 && setsEqual(selectedSet, toIdSet(allIds));

  if (
    matchesAll &&
    matchesPage &&
    activeMode === BulkGridSelectionModeEnum.Page
  ) {
    return BulkGridSelectionModeEnum.Page;
  }

  if (matchesAll) {
    return BulkGridSelectionModeEnum.All;
  }

  const rowCountSet = rowCountIds.length ? toIdSet(rowCountIds) : undefined;
  const matchesRowCount =
    rowCountSet !== undefined && setsEqual(selectedSet, rowCountSet);

  if (matchesPage && matchesRowCount) {
    if (activeMode === BulkGridSelectionModeEnum.Rows) {
      return BulkGridSelectionModeEnum.Rows;
    }

    return BulkGridSelectionModeEnum.Page;
  }

  if (matchesRowCount) {
    return BulkGridSelectionModeEnum.Rows;
  }

  if (matchesPage) {
    return BulkGridSelectionModeEnum.Page;
  }

  return undefined;
};

export interface IUngroupedPageSelectionSnapshot {
  pageIndex: number;
  pageSize: number;
  ids: readonly number[];
}

export const resolveUngroupedPageSelectionAfterChange = ({
  mode,
  snapshot,
  nextPagination,
}: {
  mode: BulkGridSelectionModeEnum;
  snapshot: IUngroupedPageSelectionSnapshot | null;
  nextPagination: PaginationState | null;
}): number[] | undefined => {
  if (mode !== BulkGridSelectionModeEnum.Page || !snapshot || !nextPagination) {
    return undefined;
  }

  if (
    snapshot.pageIndex === nextPagination.pageIndex &&
    snapshot.pageSize === nextPagination.pageSize
  ) {
    return [...snapshot.ids];
  }

  return [];
};

export const shouldResetBulkSelectionOnGroupingChange = ({
  previousGroupingKey,
  nextGroupingKey,
  isRowsUngroupInProgress,
}: {
  previousGroupingKey: string | null;
  nextGroupingKey: string;
  isRowsUngroupInProgress: boolean;
}): boolean => {
  if (previousGroupingKey === null || previousGroupingKey === nextGroupingKey) {
    return false;
  }

  return !isRowsUngroupInProgress;
};

export const shouldResetBulkSelectionOnSortingChange = ({
  previousSortingKey,
  nextSortingKey,
  activeMode,
}: {
  previousSortingKey: string | null;
  nextSortingKey: string;
  activeMode: BulkGridSelectionModeEnum;
}): boolean => {
  if (previousSortingKey === null || previousSortingKey === nextSortingKey) {
    return false;
  }

  return activeMode === BulkGridSelectionModeEnum.Rows;
};

export const toTableItemPaginationGroupKey = (
  groupValue:
    IExtendableDto[keyof IExtendableDto] | typeof GROUPING_GRID_NO_GROUPS,
): string => {
  if (groupValue === undefined || groupValue === null) {
    return "";
  }

  return String(groupValue);
};

export const sortTableRowsByColumnSorting = ({
  rows,
  sorting,
}: {
  rows: readonly IExtendableDto[];
  sorting: ColumnSort[];
}): IExtendableDto[] => {
  if (!rows.length) {
    return [];
  }

  if (!sorting.length) {
    return [...rows];
  }

  const sorted = [...rows];

  sorted.sort((rowA, rowB) => {
    for (const sort of sorting) {
      const valueA = get(rowA, sort.id);
      const valueB = get(rowB, sort.id);
      const compare =
        typeof valueA === "string" || typeof valueB === "string"
          ? compareText(valueA, valueB)
          : compareBasic(valueA, valueB);

      if (compare !== 0) {
        return sort.desc ? -compare : compare;
      }
    }

    return 0;
  });

  return sorted;
};

export const sliceRowIdsForPagination = ({
  rows,
  pagination,
  isGroupedTable,
}: {
  rows: readonly IExtendableDto[];
  pagination: PaginationState | null | undefined;
  isGroupedTable: boolean;
}): number[] => {
  if (!rows.length) {
    return [];
  }

  const pageIndex = pagination?.pageIndex ?? 0;
  const defaultPageSize = isGroupedTable
    ? TABLE_PAGE_SIZE_GROUPING
    : TABLE_PAGE_SIZE_UNGROUPED;
  const pageSize = pagination?.pageSize ?? defaultPageSize;
  const start = pageIndex * pageSize;

  return rows.slice(start, start + pageSize).map(row => row.id);
};

export const sliceFirstRowIds = ({
  rows,
  count,
}: {
  rows: readonly IExtendableDto[];
  count: number;
}): number[] => {
  if (!rows.length || count <= 0) {
    return [];
  }

  const limit = Math.min(count, rows.length);

  return rows.slice(0, limit).map(row => row.id);
};

/** First N ids in the same order the grid shows (loaded rows at list start). */
export const resolveFirstNRowIdsFromVisibleGrid = ({
  visibleRowIds,
  count,
  pageIndex,
}: {
  visibleRowIds: readonly number[];
  count: number;
  pageIndex: number;
}): number[] | undefined => {
  if (count <= 0 || pageIndex !== 0 || visibleRowIds.length < count) {
    return undefined;
  }

  return visibleRowIds.slice(0, count);
};

/** Server-paginated grids only hold the active page(s) in memory — use loaded rows as the page scope. */
export const collectServerPaginatedCurrentPageRowIds = ({
  groupValues,
  groupedRowsMap,
  groupingFieldNameResolved,
}: {
  groupValues: readonly (
    IExtendableDto[keyof IExtendableDto] | typeof GROUPING_GRID_NO_GROUPS
  )[];
  groupedRowsMap: TTableFilteredGroupedRowsMap;
  groupingFieldNameResolved: string | undefined;
}): number[] => {
  const ids: number[] = [];

  for (const groupValue of groupValues) {
    const rows = getTableFilteredGroupedRowsFromMap({
      groupedRowsMap,
      groupingFieldValue: groupValue,
      groupingFieldNameResolved,
    });

    for (const row of rows) {
      ids.push(row.id);
    }
  }

  return ids;
};

export const resolveBulkSelectionAllIds = ({
  isServerPaginated,
  serverScopeIds,
  visibleRowIds,
}: {
  isServerPaginated: boolean;
  serverScopeIds: readonly number[];
  visibleRowIds: readonly number[];
}): number[] => {
  if (isServerPaginated) {
    return [...serverScopeIds];
  }

  return [...visibleRowIds];
};

export type PassengerBulkSelectionGroupFilter = {
  filterByField?: string;
  filterByValue?: string | number | boolean | null;
};

export const resolvePassengerBulkSelectionGroupFilter = ({
  groupingField,
  groupingValue,
}: {
  groupingField: string | number | undefined;
  groupingValue: unknown;
}): PassengerBulkSelectionGroupFilter => {
  if (
    groupingField === undefined ||
    groupingField === null ||
    groupingValue === GROUPING_GRID_NO_GROUPS
  ) {
    return {};
  }

  const filterByValue =
    groupingValue === null ||
    typeof groupingValue === "string" ||
    typeof groupingValue === "number" ||
    typeof groupingValue === "boolean"
      ? groupingValue
      : String(groupingValue);

  const groupFilter = {
    filterByField: String(groupingField),
    filterByValue,
  } satisfies PassengerBulkSelectionGroupFilter;

  return groupFilter;
};

export const buildPassengerBulkSelectionIdsSearchBody = ({
  where,
  columnSorting,
  mode,
  rowCount,
  filterByField,
  filterByValue,
}: {
  where: Record<string, unknown>;
  columnSorting: ColumnSort[];
  mode: BulkGridSelectionModeEnum;
  rowCount: number;
  filterByField?: string;
  filterByValue?: string | number | boolean | null;
}): ISearchCriteria<IExtendableDto> => {
  const orderByField = tableHelpers.getOrderByField(columnSorting);
  const orderByDirection = tableHelpers.getOrderByDirection(columnSorting);
  const groupFilter = filterByField
    ? { filterByField, filterByValue }
    : undefined;

  if (mode === BulkGridSelectionModeEnum.Rows && rowCount > 0) {
    const rowCountBody = {
      where,
      orderByField,
      orderByDirection,
      take: rowCount,
      skip: 0,
      ...groupFilter,
    } satisfies ISearchCriteria<IExtendableDto>;

    return rowCountBody;
  }

  const allIdsBody = {
    where,
    orderByField,
    orderByDirection,
    ...groupFilter,
  } satisfies ISearchCriteria<IExtendableDto>;

  return allIdsBody;
};
